import styled from 'styled-components';

import AIButton from 'components/shared/ai/AIButton';
import Jumbotron from 'components/shared/ai/Jumbotron';
import { useEpisode } from 'context/episode';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import { BodySmallBold } from 'styles/typography';
import AIChecklistIcon from 'svg/AIChecklistIcon';

type SummaryEmptyStateProps = {
  approvalThroughDate?: string;
  lastUpdatedDate?: string;
  onCompareSummaries: () => void;
  isSummarized: boolean;
};

export default function ComparisonEmptyState({
  approvalThroughDate,
  lastUpdatedDate,
  onCompareSummaries,
  isSummarized,
}: SummaryEmptyStateProps) {
  // Check if AI comparison buttons should be disabled
  const { profile } = useProfile();
  const { episode } = useEpisode();
  const { locationEpisode } = useSelectedLocationEpisode();

  const canCreateComparison = profile?.has(PERMISSIONS.aiJobComparisonCreate, { episode, locationEpisode }) ?? false;
  const isLocationEpisodeDischarged = locationEpisode?.discharged ?? false;
  const shouldDisableComparisonButton = !isSummarized || !canCreateComparison || isLocationEpisodeDischarged;

  return (
    <StyledJumbotron>
      <JumbotronContentContainer>
        <AIChecklistIcon />
        <BodySmallBold>Compare to {lastUpdatedDate ?? 'N/A'} Summary</BodySmallBold>
        {isSummarized ? (
          <BodyText>Compare what was changed, added or removed between summaries</BodyText>
        ) : (
          <BodyText>
            Before comparing, a summary must exist for {approvalThroughDate ?? 'N/A'} and {lastUpdatedDate ?? 'N/A'}
          </BodyText>
        )}
      </JumbotronContentContainer>
      <AIButton data-cy='CompareSummaries' disabled={shouldDisableComparisonButton} onClick={onCompareSummaries}>
        Compare Summaries
      </AIButton>
    </StyledJumbotron>
  );
}

const StyledJumbotron = styled(Jumbotron)`
  height: 220px;
  gap: 24px;
  padding: 24px;
`;

const JumbotronContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const BodyText = styled.p`
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 2%;
`;
